import { faArrowTurnDownLeft } from "@fortawesome/pro-regular-svg-icons";
import { twJoin, twMerge } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";

/**
 * Presentational building blocks of the omnibox — the elevated surface, a
 * scrollable list, group headings and rows — also composed by the copilot `@`
 * mention menu. Stateless by design: the consumer owns filtering, highlighting
 * and key handling and tells each `Item` whether it is highlighted.
 */

type OmniboxProps = {
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  dataLoc?: string;
  /** Layout only (positioning, width). The surface styling is fixed. */
  className?: string;
};

const LIST_MAX_HEIGHT = {
  sm: "max-h-64",
  lg: "max-h-96",
} as const;

/** DOM and aria attributes a consumer (or a menu library) may forward to the list. */
export type OmniboxListDomProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "className" | "style" | "children"
>;

type OmniboxListProps = {
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
  dataLoc?: string;
  maxHeight?: keyof typeof LIST_MAX_HEIGHT;
} & OmniboxListDomProps;

const OmniboxList: React.FC<OmniboxListProps> = ({
  children,
  ref,
  dataLoc,
  maxHeight = "lg",
  role = "listbox",
  ...domProps
}) => (
  <div
    ref={ref}
    {...domProps}
    className={twJoin(
      "decideScrollbar overflow-y-auto p-2",
      LIST_MAX_HEIGHT[maxHeight],
    )}
    data-loc={dataLoc}
    role={role}
  >
    {children}
  </div>
);

type OmniboxGroupHeadingProps = {
  children: React.ReactNode;
  dataLoc?: string;
};

const OmniboxGroupHeading: React.FC<OmniboxGroupHeadingProps> = ({
  children,
  dataLoc,
}) => (
  <div
    className="h-8 w-full truncate px-2 py-2 font-bold font-inter-medium-11px text-gray-600"
    data-loc={dataLoc}
    role="presentation"
  >
    {children}
  </div>
);

/** DOM and aria attributes a consumer (or a menu library) may forward to a row. */
export type OmniboxItemDomProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "className" | "style" | "disabled" | "children"
>;

type OmniboxItemProps = {
  /** Leading visual, rendered in a fixed 24px slot (e.g. a `NodeIcon`). */
  icon?: React.ReactNode;
  label: React.ReactNode;
  /** Right-aligned secondary text such as the item's type. Replaced by the ↵ hint while highlighted. */
  meta?: string;
  isHighlighted?: boolean;
  isDisabled?: boolean;
  dataLoc?: string;
  ref?: React.Ref<HTMLButtonElement>;
} & OmniboxItemDomProps;

const OmniboxItem: React.FC<OmniboxItemProps> = ({
  icon,
  label,
  meta,
  isHighlighted = false,
  isDisabled = false,
  dataLoc,
  ref,
  role = "option",
  "aria-selected": ariaSelected = isHighlighted,
  ...buttonProps
}) => (
  <button
    ref={ref}
    {...buttonProps}
    aria-selected={ariaSelected}
    className={twJoin(
      "flex h-10 w-full items-center justify-between gap-4 overflow-hidden rounded-md p-2 text-gray-900",
      isHighlighted && !isDisabled && "bg-gray-100 bg-opacity-80",
      isDisabled && "cursor-not-allowed opacity-40",
    )}
    data-loc={dataLoc}
    disabled={isDisabled}
    role={role}
    type="button"
  >
    <span className="flex min-w-0 flex-1 items-center gap-x-2">
      {icon && (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-grow overflow-hidden text-ellipsis whitespace-nowrap text-left font-inter-medium-13px">
        {label}
      </span>
    </span>
    <span className="flex shrink-0 items-center">
      {isHighlighted && !isDisabled ? (
        <Icon icon={faArrowTurnDownLeft} size="xs" />
      ) : (
        meta && (
          <span className="whitespace-nowrap text-right font-inter-normal-12px text-gray-400">
            {meta}
          </span>
        )
      )}
    </span>
  </button>
);

/**
 * The elevated white surface. Consumers position and size it (modal panel,
 * anchored popup) and compose `Omnibox.List`, `.GroupHeading` and `.Item`
 * inside.
 */
export const Omnibox = (({
  children,
  ref,
  dataLoc,
  className,
}: OmniboxProps) => (
  <div
    ref={ref}
    className={twMerge(
      "flex flex-col overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-gray-100 ring-opacity-50",
      className,
    )}
    data-loc={dataLoc}
  >
    {children}
  </div>
)) as React.FC<OmniboxProps> & {
  List: typeof OmniboxList;
  GroupHeading: typeof OmniboxGroupHeading;
  Item: typeof OmniboxItem;
};

Omnibox.List = OmniboxList;
Omnibox.GroupHeading = OmniboxGroupHeading;
Omnibox.Item = OmniboxItem;
